import React, { FC } from 'react'
import kebabCase from 'lodash'
import { toFeatureState } from '@west-australian-newspapers/etrigan-feature-toggles-client'
import {
    DataLayerEventName,
    isComponentEnabled,
    NavEventArgs,
    NavItem,
    usePageEvents,
    useToggleState,
    useFeature,
} from '@news-mono/web-common'
import { TNQuickLinksProps } from './TNQuickLinks'
import {
    StyledLink,
    StyledNav,
    StyledNavItem,
    StyledNavList,
    TNStyledQuickLinks,
} from './TNQuickLinks.styled'
import { useLatestOnEdition } from '../../__helpers'

export const TNQuickLinksWeb: FC<TNQuickLinksProps> = React.memo(
    ({ links, onEvent, hasBackgroundFill, services }) => {
        const { toggles } = services.store.getState()
        usePageEvents(() => {
            onEvent({
                type: DataLayerEventName.navAvailable,
                originator: 'TNQuickLinks',
                payload: {},
            })
        }, ['page-load-complete'])

        const handleClick = (args: NavEventArgs) => {
            onEvent({
                type: DataLayerEventName.navClicked,
                originator: 'TNQuickLinks',
                payload: {
                    navName: 'TNQuickLinks',
                    navLocation: 'Header',
                    navPos: args.navPos,
                    navText: args.navText,
                    navLink: args.navLink,
                },
            })
        }

        const featureState = useToggleState()
        const federalElectionNavEnabled = useFeature(
            'federal-election-sitewide-navigation',
        )

        const { latestOnEdition } = useLatestOnEdition(services)

        const navLinks = links.map((link) => {
            const shouldUpdateOnEditionLink =
                latestOnEdition &&
                link.name === 'The Nightly On' &&
                latestOnEdition.primaryTopic
            if (shouldUpdateOnEditionLink) {
                return {
                    ...link,
                    // we want The Nightly On link always point to the latest On edition
                    link: `/${latestOnEdition.primaryTopic}`,
                }
            }
            return link
        })

        return (
            <TNStyledQuickLinks
                hasBackgroundFill={hasBackgroundFill || false}
                isCompact={federalElectionNavEnabled}
            >
                <StyledNav>
                    <StyledNavList>
                        {navLinks
                            .filter((item: NavItem) => {
                                if (
                                    item.name &&
                                    item.name === 'Politics' &&
                                    federalElectionNavEnabled
                                ) {
                                    return false
                                }
                                if (item.feature) {
                                    return isComponentEnabled(
                                        toFeatureState(featureState),
                                        {
                                            feature: item.feature,
                                            invertFeatureToggle:
                                                item.invertFeatureToggle,
                                        },
                                    )
                                }
                                return true
                            })
                            .map((item, index) => (
                                <StyledNavItem
                                    key={item.link}
                                    isCompact={federalElectionNavEnabled}
                                >
                                    <StyledLink
                                        to={item.link}
                                        target={
                                            item.newWindow
                                                ? '_blank'
                                                : undefined
                                        }
                                        rel={
                                            item.newWindow
                                                ? 'noopener'
                                                : undefined
                                        }
                                        onClick={() => {
                                            handleClick({
                                                navPos: index,
                                                navText: item.name,
                                                navLink: item.link,
                                            })
                                        }}
                                        id={`${kebabCase(
                                            item.name,
                                        ).lowerCase()}-quicklink`}
                                    >
                                        {item.name}
                                    </StyledLink>
                                </StyledNavItem>
                            ))}
                    </StyledNavList>
                </StyledNav>
            </TNStyledQuickLinks>
        )
    },
)
TNQuickLinksWeb.displayName = 'TNQuickLinksWeb'
